import { Service } from "@/apps/credit-line/services/network.service";
import {
  IReportCardTransactionRequest,
  IFetchCardTransactionReportRequest,
} from "@/apps/credit-line/types/requests";
import {
  ReportCardTransactionResponse,
  FetchTransactionReportResponse,
} from "@/apps/credit-line/types/responses";
const creditLineApiService = new Service();

export interface ICreateReportPayload {
  deviceTag: string;
  reportReason: string;
  debitId: number | string; // Allow both number and string for flexibility
}

export interface ICreateReportResponse {
  reportId?: number;
}

export interface IFetchTransactionReportPayload {
  deviceTag: string;
  debitId: number | string; // Allow both number and string for flexibility
}

export interface Report {
  reportStatus?: TTransactionReportStatus;
  creditCardTransactionId: number;
  reason: string;
  reportId: number;
  reportedTime: number;
}

export interface IFetchTransactionReportResponse {
  report: Report | null;
}

export type TTransactionReportStatus = "UNDER_REVIEW" | "SOLVED";

export const reportTransaction = async (
  payload: IReportCardTransactionRequest
): Promise<ReportCardTransactionResponse> => {
  try {
    // Ensure debitId is always sent as a number to the API
    const processedPayload = {
      ...payload,
      debitId:
        typeof payload.debitId === "string"
          ? parseInt(payload.debitId, 10)
          : payload.debitId,
    };

    const response = await creditLineApiService.reportCardTransaction(
      processedPayload
    );

    return response.data as ReportCardTransactionResponse;
  } catch (error: unknown) {
    const err = error as {
      response?: {
        status: number;
        data?: Record<string, unknown>;
        headers?: Record<string, string>;
      };
    };
    if (err.response) {
      console.error("Error details:", {
        status: err.response.status,
        data: err.response.data,
        headers: err.response.headers,
      });
    }
    throw error;
  }
};

export const fetchTransactionReport = async (
  payload: IFetchCardTransactionReportRequest
): Promise<FetchTransactionReportResponse> => {
  try {
    // Ensure debitId is always sent as a number to the API
    const processedPayload = {
      ...payload,
      debitId:
        typeof payload.debitId === "string"
          ? parseInt(payload.debitId, 10)
          : payload.debitId,
    };

    const response = await creditLineApiService.fetchCardTransactionReport(
      processedPayload
    );

    //console.log("fetchTransactionReport - Response:", response.data);
    return response.data as FetchTransactionReportResponse;
  } catch (error: unknown) {
    //console.error("fetchTransactionReport - Error:", error);

    // Check if this is error code 784 ("no report found for card transaction")
    // This is an expected case when a user hasn't reported a transaction yet
    const err = error as {
      response?: {
        status: number;
        data?: { errorCode?: number; message?: string; instance?: string };
        headers?: Record<string, string>;
      };
    };
    if (err.response?.data?.errorCode === 784) {
      /*console.log(
        "No existing report found (error code 784) - this is an expected case"
      );*/
      // Return an empty response object to indicate no report exists
      return { report: null } as unknown as FetchTransactionReportResponse;
    }

    if (err.response) {
      console.error("Error details:", {
        status: err.response.status,
        data: err.response.data,
        headers: err.response.headers,
      });
    }
    throw error;
  }
};
